import React, { useMemo } from 'react';
import Fade from 'react-reveal/Fade';
import Tilt from 'react-tilt';
import { Container, Row, Col } from 'react-bootstrap';
import { nanoid } from 'nanoid';
import { useStaticQuery, graphql } from 'gatsby';
import Title from '../Title/Title';
import ProjectImg from '../Image/ProjectImg';
import useLocalize from '../../common/intl/useLocalize';
import useDevice from '../../common/device/useDevice';
import { DEFAULT_DELAY, DEFAULT_DURATION } from '../../common/constants';

const Projects = () => {
  const localize = useLocalize();

  const apkFiles = useStaticQuery(graphql`
    query {
      apk: allFile {
        edges {
          node {
            name
            publicURL
          }
        }
      }
    }
  `);

  const shakshukaData = React.useMemo(
    () => apkFiles.apk.edges.find((f) => f.node.name.includes('shakshuka')),
    [apkFiles]
  );

  const projects = useMemo(
    () => [
      {
        id: nanoid(),
        img: 'skupka-avto.webp',
        title: localize('projects-skupka-avto-title'),
        info: localize('projects-skupka-avto-info'),
        info2: '',
        url: 'https://skupka-avto.by/',
        repo: '', // if no repo, the button will not show up
      },
      {
        id: nanoid(),
        img: 'service-avto.webp',
        title: localize('projects-service-avto-title'),
        info: localize('projects-service-avto-info'),
        info2: '',
        url: 'http://сервис-авто.бел/',
        repo: '', // if no repo, the button will not show up
      },
      {
        id: nanoid(),
        img: 'shakshuka.webp',
        title: localize('projects-shakshuka-title'),
        info: localize('projects-shakshuka-info'),
        info2: '',
        url: '',
        download: shakshukaData.node.publicURL,
        repo: '', // if no repo, the button will not show up
      },
    ],
    [localize, shakshukaData]
  );

  const { isDesktop, isMobile } = useDevice();

  return (
    <section id="projects">
      <Container>
        <div className="project-wrapper">
          <Title title={localize('projects-projects')} />
          {projects.map((project) => {
            const { title, info, info2, url, repo, img, id, download } = project;

            return (
              <Row key={id}>
                <Col lg={4} sm={12}>
                  <Fade
                    left={isDesktop}
                    bottom={isMobile}
                    duration={DEFAULT_DURATION}
                    delay={DEFAULT_DELAY}
                    distance="30px"
                  >
                    <div className="project-wrapper__text">
                      <h3 className="project-wrapper__text-title">{title || ''}</h3>
                      <div>
                        <p>{info || ''}</p>
                        <p className="mb-4">{info2 || ''}</p>
                      </div>
                      {url && (
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cta-btn cta-btn--hero"
                          href={url}
                        >
                          {localize('projects-see-live')}
                        </a>
                      )}
                      {download && (
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cta-btn cta-btn--hero"
                          href={download}
                        >
                          {localize('projects-download')}
                        </a>
                      )}
                      {repo && (
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cta-btn text-color-main"
                          href={repo}
                        >
                          {localize('projects-source-code')}
                        </a>
                      )}
                    </div>
                  </Fade>
                </Col>
                <Col lg={8} sm={12}>
                  <Fade
                    right={isDesktop}
                    bottom={isMobile}
                    duration={DEFAULT_DURATION}
                    delay={DEFAULT_DELAY * 2}
                    distance="30px"
                  >
                    <div className="project-wrapper__image">
                      <a
                        href={url || '#!'}
                        target={url ? '_blank' : ''}
                        aria-label="Project Link"
                        rel="noopener noreferrer"
                      >
                        <Tilt
                          options={{
                            reverse: false,
                            max: 8,
                            perspective: 1000,
                            scale: 1,
                            speed: 300,
                            transition: true,
                            axis: null,
                            reset: true,
                            easing: 'cubic-bezier(.03,.98,.52,.99)',
                          }}
                        >
                          <div data-tilt className="thumbnail rounded">
                            <ProjectImg alt={title} filename={img} />
                          </div>
                        </Tilt>
                      </a>
                    </div>
                  </Fade>
                </Col>
              </Row>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

export default Projects;
